import { useContext, useMemo, useState } from 'react';
import { ComplianceCheckDialog } from '@modules/mocks/complianceCheck';
import { FoldersNavDrawer, SelectItemDialog, TItem, TSelectItemDialogState } from '@modules/items';
import { AccessType } from '@modules/access';
import { ControlledTooltip, Path } from '@shared/components';

import {
  ESubmissionStatus,
  TResolvedSubmissionItem,
  TSingleSubmissionData,
  TSubmissionSelectableItemType,
} from '../../types/submissions.types';
import { FULL_PATH_SUBMISSIONS_ITEM } from '../../constants';

import SubmissionDetailsCard from './cards/SubmissionDetailsCard';
import SubmissionReadinessCard from './cards/SubmissionReadinessCard';
import SubmissionRecentActivitiesCard from './cards/SubmissionRecentActivitiesCard';
import { Button, Subtitle1 } from '@fluentui/react-components';
import { useSelectSubmissionItems } from '@modules/submissions/api';
import SelectedItems from './items/SelectedItems';
import { BrainSparkleRegular, SelectAllOn24Regular } from '@fluentui/react-icons';
import { useHasFullPermissions } from '../../helpers/getPermissions.helper';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { SignInContext } from '@modules/users';

type TProps = {
  data: TSingleSubmissionData;
};

const SingleSubmissionContent = ({ data }: TProps) => {
  const {
    folder,
    submissionData: { submission, resolvedItems },
  } = data;
  const { organization } = useContext(SignInContext);

  // Build navigation fullPath for the FoldersNavDrawer
  const fullPath = [
    { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
    { ...FULL_PATH_SUBMISSIONS_ITEM, href: `/product/${folder._id}/submissions` },
    { _id: submission._id, name: submission.title, accessRight: AccessType.NONE },
  ];

  const selectSubmissionItems = useSelectSubmissionItems(submission._id);

  // Local state for showing the dialog.
  const [selectDialogState, setSelectDialogState] = useState<TSelectItemDialogState | undefined>({
    isOpen: false,
    onSelect: async (newSelectedItems: TItem[]) => {
      // Convert your chosen TItem array into { itemId, itemType } objects
      const selectedItems = newSelectedItems.map((item) => ({
        itemId: item._id,
        itemType: item.type as TSubmissionSelectableItemType,
      }));

      // Fire the mutation
      selectSubmissionItems.mutate(selectedItems);

      // Close the dialog
      setSelectDialogState((prev) => prev && { ...prev, isOpen: false });
    },
  });
  const [isComplianceCheckOpen, setIsComplianceCheckOpen] = useState(false);

  // Filter out those items that have either been deleted or manually deselected + build full selected items:
  const resolvedSelectedItems: TResolvedSubmissionItem[] = useMemo(
    () =>
      submission.selectedItems
        .filter((submissionItem) => resolvedItems[submissionItem.item] && submissionItem.active)
        .map((submissionItem) => ({ ...submissionItem, resolvedItem: resolvedItems[submissionItem.item].item })),
    [submission.selectedItems, resolvedItems]
  );

  const jurisdictionItem = useMemo(
    () => folder.jurisdictions.find((j) => j._id === submission.jurisdiction),
    [submission.jurisdiction, folder.jurisdictions]
  );

  // open “select item dialog” in product folder if the jurisdiction is missing (i.e. has been archived)
  const initialFolderId = jurisdictionItem ? submission.jurisdiction || folder._id : folder._id;

  // permissions:
  const hasFullPermissions = useHasFullPermissions(submission);
  const isAiEnabled = organization.mock.isAiEnabled;

  return (
    <div className="flex flex-1 h-full">
      {/* Navigation drawer */}
      <FoldersNavDrawer fullPath={fullPath} selectedValue="submissions" folder={folder} />

      {/* Main content */}
      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col pt-4 pb-16 pl-5 pr-8" /* max-w-screen-xl */>
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
            <div className="flex space-x-8 mt-4">
              <SubmissionDetailsCard folder={folder} submission={submission} jurisdictionItem={jurisdictionItem} />
              <SubmissionReadinessCard resolvedSelectedItems={resolvedSelectedItems} />
              <SubmissionRecentActivitiesCard resolvedSelectedItems={resolvedSelectedItems} />
            </div>

            <div className="mt-10">
              <p className="flex items-center gap-4">
                <Subtitle1>
                  {resolvedSelectedItems.length} file{resolvedSelectedItems.length === 1 ? '' : 's'} selected
                </Subtitle1>
                <ControlledTooltip
                  content={
                    submission.status !== ESubmissionStatus.IN_PREPARATION
                      ? 'Submission is not in status "In preparation".'
                      : !hasFullPermissions
                        ? 'You do not have the required permissions.'
                        : ''
                  }
                  relationship="description"
                >
                  <Button
                    icon={<SelectAllOn24Regular />}
                    appearance="primary"
                    onClick={() => setSelectDialogState((prev) => (prev ? { ...prev, isOpen: true } : prev))}
                    disabled={submission.status !== ESubmissionStatus.IN_PREPARATION || !hasFullPermissions}
                  >
                    Update selection
                  </Button>
                </ControlledTooltip>
                {isAiEnabled && (
                  <Button
                    icon={<BrainSparkleRegular />}
                    appearance="secondary"
                    onClick={() => setIsComplianceCheckOpen(true)}
                  >
                    Compliance check
                  </Button>
                )}
              </p>

              {resolvedSelectedItems.length > 0 && (
                <div className="mt-4">
                  <SelectedItems submission={submission} resolvedSelectedItems={resolvedSelectedItems} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SelectItemDialog
        // Open the jurisdiction folder or otherwise the product folder
        initialFolderId={initialFolderId}
        // Note: folders selected by the user are resolved to individual documents/files on the backend,
        // so only those concrete items can be pre-selected here. Original folder selections are not persisted.
        initialSelectedItems={resolvedSelectedItems.map((item) => item.resolvedItem)}
        state={selectDialogState}
        setState={setSelectDialogState}
        title="Select files for submission"
        actionText="Confirm"
        navigatableFolderTypes={['NORMAL', 'COUNTRY']}
        selectableTypes={['FOLDER', 'DOCUMENT', 'FILE', 'REPLICA_DOCUMENT', 'REPLICA_FILE']}
        multiSelect
      />
      <ComplianceCheckDialog
        open={isComplianceCheckOpen}
        setOpen={setIsComplianceCheckOpen}
        productId={folder._id}
        submissionId={submission._id}
      />
    </div>
  );
};

export default SingleSubmissionContent;
